import i18n from '../i18n/config';
import { useTranslation } from 'react-i18next';
// frontend/src/pages/TasksPage.jsx
// Dashboard + listado de tareas APS. Permite crear, filtrar, ver detalle, transicionar.
import React, { useEffect, useMemo, useState } from 'react';
import {
    Alert, Autocomplete, Box, Button, Card, CardContent, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
    DialogTitle, Divider, FormControl, Grid, IconButton, InputAdornment, InputLabel, MenuItem, Paper,
    Select, Stack, Tab, Tabs, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination,
    TableRow, TextField, Tooltip, Typography
} from '@mui/material';
import {
    Add as AddIcon, Search as SearchIcon, Refresh as RefreshIcon, ArrowForward as ArrowIcon,
    AssignmentTurnedIn as DoneIcon, Cancel as CancelIcon, PlayArrow as StartIcon, Pause as BlockedIcon,
    PriorityHigh as CriticalIcon, AccessTime as ClockIcon, Person as PersonIcon, Build as MaintIcon,
    WarningAmber as WarningIcon, AutoMode as AutoIcon, Edit as EditIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { api } from '../services/api';
import { useNotification } from '../components/common/GlobalNotifications';
import AIRecommendationsPanel from '../components/ai/AIRecommendationsPanel';
import AskAIButton from '../components/ai/AskAIButton';

const STATUS_COLORS = {
    pending: 'default',
    in_progress: 'info',
    blocked: 'warning',
    completed: 'success',
    cancelled: 'default',
};
// Las etiquetas visibles se resuelven EN CADA RENDER, no al importar el modulo.
// Antes eran constantes con i18n.t(...) evaluadas al cargar el archivo: quedaban
// congeladas en el idioma que hubiera en ese instante, y el idioma del perfil se
// aplica DESPUES (cambiar idioma no recarga la pagina). Por eso podian verse
// textos en otro idioma aunque el usuario tuviera espanol.
const STATUS_LABEL_KEYS = {
    pending: 'hardcoded.components_admin_ordermanagement.pendiente',
    in_progress: 'hardcoded.pages_checkoutpage.en_progreso',
    blocked: 'hardcoded.components_tickets_ticketactioncenter.bloqueada',
    completed: 'hardcoded.components_tickets_ticketactioncenter.completada',
    cancelled: 'hardcoded.components_admin_ordermanagement.cancelada',
};
const statusLabel = (status) =>
    (STATUS_LABEL_KEYS[status] ? i18n.t(STATUS_LABEL_KEYS[status]) : status);
const statusEntries = () =>
    Object.keys(STATUS_LABEL_KEYS).map((value) => [value, statusLabel(value)]);

const PRIORITY_COLORS = {
    low: 'default',
    medium: 'info',
    high: 'warning',
    critical: 'error',
};
const PRIORITY_LABEL_KEYS = {
    low: 'hardcoded.pages_taskspage.priority_low',
    medium: 'hardcoded.pages_taskspage.priority_medium',
    high: 'hardcoded.pages_alarmticketpage.alta',
    critical: 'alarmTickets.severity.critical',
};
const priorityLabel = (priority) =>
    (PRIORITY_LABEL_KEYS[priority] ? i18n.t(PRIORITY_LABEL_KEYS[priority]) : priority);
const priorityEntries = () =>
    Object.keys(PRIORITY_LABEL_KEYS).map((value) => [value, priorityLabel(value)]);

// `value` es el dato que viaja al backend (enum TaskCategory): jamas se traduce.
const CATEGORY_KEYS = [
    ['general', 'hardcoded.pages_taskspage.category_general'],
    ['maintenance', 'hardcoded.pages_taskspage.mantenimiento'],
    ['inspection', 'hardcoded.pages_taskspage.inspeccion'],
    ['pressure', 'hardcoded.components_alarmprofiles_profilecard.presion'],
    ['temperature', 'hardcoded.components_alarmprofiles_profilecard.temperatura'],
    ['dual_yoyo', 'hardcoded.components_alarmprofiles_profileform.dual'],
    ['no_report', 'hardcoded.components_schematics_vehicleticketsdrawer.sin_reporte'],
    ['sensor', 'hardcoded.components_schematics_vehicleticketsdrawer.sensor'],
    ['receiver', 'hardcoded.components_schematics_receivereventsmodal.receptor'],
    ['administrative', 'hardcoded.pages_taskspage.administrativa'],
];
const categoryOptions = () =>
    CATEGORY_KEYS.map(([value, key]) => ({ value, label: i18n.t(key) }));

const formatDate = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso);
    return d.toLocaleString('es-MX', { dateStyle: 'short', timeStyle: 'short' });
};

// Aplana vehículos padres + child_vehicles para que TODOS aparezcan en el dropdown.
const flattenVehicles = (vehicles = []) => {
    const out = [];
    const walk = (list) => {
        list.forEach((v) => {
            out.push(v);
            if (v.child_vehicles?.length) walk(v.child_vehicles);
        });
    };
    walk(vehicles);
    return out;
};

const TaskCreateDialog = ({ open, onClose, onCreated, defaults = {} }) => {
  const { t } = useTranslation();
    const { showError, showSuccess } = useNotification();
    const [saving, setSaving] = useState(false);
    const [users, setUsers] = useState([]);
    const [vehicles, setVehicles] = useState([]);
    const [form, setForm] = useState({
        title: '', description: '', priority: 'medium', category: 'general',
        due_at: '', assigned_to: '', vehicle_id: '', ticket_id: '', position: '', axle: '',
        ...defaults,
    });

    useEffect(() => {
        if (!open) return;
        setForm({ title: '', description: '', priority: 'medium', category: 'general', due_at: '', assigned_to: '', vehicle_id: '', ticket_id: '', position: '', axle: '', ...defaults });
        // Cargar usuarios y vehículos para los selectores
        api.users.getAll().then((r) => setUsers(r.data || [])).catch(() => setUsers([]));
        api.vehicles.getAll().then((r) => setVehicles(flattenVehicles(r.data || []))).catch(() => setVehicles([]));
    }, [open]); // eslint-disable-line

    const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

    // Vehículo seleccionado — para derivar posiciones y ejes válidos
    const selectedVehicle = useMemo(() => vehicles.find((v) => v.id === form.vehicle_id), [vehicles, form.vehicle_id]);
    const positions = useMemo(() => {
        if (!selectedVehicle) return [];
        const total = Number(selectedVehicle.positions_count) || 0;
        return Array.from({ length: total }, (_, i) => i + 1);
    }, [selectedVehicle]);
    const axles = useMemo(() => {
        if (!selectedVehicle) return [];
        const total = Number(selectedVehicle.axle_count) || 0;
        return Array.from({ length: total }, (_, i) => i + 1);
    }, [selectedVehicle]);

    // Cuando cambia el vehículo, limpiar posición/eje que pudieran no aplicar.
    useEffect(() => {
        setForm((p) => {
            const next = { ...p };
            if (next.position && !positions.includes(Number(next.position))) next.position = '';
            if (next.axle && !axles.includes(Number(next.axle))) next.axle = '';
            return next;
        });
    }, [form.vehicle_id]); // eslint-disable-line

    const userLabel = (u) => {
        if (!u) return '';
        const full = `${u.name || ''} ${u.last_name || ''}`.trim() || u.full_name || u.email;
        return `${full} · ${u.email}`;
    };

    const save = async () => {
        if (!form.title.trim()) { showError(t('hardcoded.components_tickets_ticketactioncenter.titulo_requerido')); return; }
        setSaving(true);
        try {
            const payload = { ...form };
            if (!payload.due_at) delete payload.due_at;
            if (!payload.assigned_to) delete payload.assigned_to;
            if (!payload.vehicle_id) delete payload.vehicle_id;
            if (!payload.ticket_id) delete payload.ticket_id;
            if (!payload.position) delete payload.position; else payload.position = Number(payload.position);
            if (!payload.axle) delete payload.axle; else payload.axle = Number(payload.axle);
            const r = await api.tasks.create(payload);
            showSuccess(t('hardcoded.components_tickets_ticketactioncenter.tarea_creada'));
            onCreated?.(r.data);
            onClose();
        } catch (e) {
            showError(e?.response?.data?.detail || t('hardcoded.components_tickets_ticketaianalysiscard.no_se_pudo_crear_la'));
        } finally { setSaving(false); }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>{i18n.t('hardcoded.pages_taskspage.nueva_tarea')}</DialogTitle>
            <DialogContent dividers>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <TextField fullWidth label={t('hardcoded.components_tickets_ticketactioncenter.titulo')} value={form.title} onChange={set('title')} autoFocus required />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField fullWidth label={t('audit.table.description')} value={form.description} onChange={set('description')} multiline rows={3} />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <FormControl fullWidth>
                            <InputLabel>{t('hardcoded.components_tickets_ticketactioncenter.prioridad')}</InputLabel>
                            <Select label={t('hardcoded.components_tickets_ticketactioncenter.prioridad')} value={form.priority} onChange={set('priority')}>
                                {priorityEntries().map(([v, l]) => (
                                    <MenuItem key={v} value={v}>{l}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <FormControl fullWidth>
                            <InputLabel>{t('hardcoded.components_admin_categoryform.categoria')}</InputLabel>
                            <Select label={t('hardcoded.components_admin_categoryform.categoria')} value={form.category} onChange={set('category')}>
                                {categoryOptions().map((c) => (
                                    <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField fullWidth type="datetime-local" label={t('hardcoded.pages_taskspage.vencimiento_opcional')} InputLabelProps={{ shrink: true }}
                            value={form.due_at} onChange={set('due_at')} />
                    </Grid>

                    {/* Vehículo: Autocomplete searcheable */}
                    <Grid item xs={12} sm={6}>
                        <Autocomplete
                            options={vehicles}
                            getOptionLabel={(o) => o?.display_name || o?.alias || o?.id || ''}
                            value={vehicles.find((v) => v.id === form.vehicle_id) || null}
                            onChange={(_, val) => setForm((p) => ({ ...p, vehicle_id: val?.id || '' }))}
                            renderInput={(params) => <TextField {...params} label={t('hardcoded.components_settings_companynetworkstab.vehiculo_opcional')} placeholder={t('hardcoded.pages_taskspage.buscar_vehiculo')} />}
                            isOptionEqualToValue={(o, v) => o.id === v.id}
                        />
                    </Grid>

                    {/* Posición — dropdown según vehículo */}
                    <Grid item xs={6} sm={3}>
                        <FormControl fullWidth disabled={!selectedVehicle || !positions.length}>
                            <InputLabel>{i18n.t('alarms.list.position')}</InputLabel>
                            <Select label={t('alarms.list.position')} value={form.position}
                                onChange={(e) => setForm((p) => ({ ...p, position: e.target.value }))}>
                                <MenuItem value=""><em>—</em></MenuItem>
                                {positions.map((n) => <MenuItem key={n} value={n}>{i18n.t('hardcoded.components_schematics_vehicletelemetrymodal.pos')}{n}</MenuItem>)}
                            </Select>
                        </FormControl>
                    </Grid>

                    {/* Eje — dropdown según vehículo */}
                    <Grid item xs={6} sm={3}>
                        <FormControl fullWidth disabled={!selectedVehicle || !axles.length}>
                            <InputLabel>{i18n.t('vehicleDetail.alarms.axle')}</InputLabel>
                            <Select label={t('vehicleDetail.alarms.axle')} value={form.axle}
                                onChange={(e) => setForm((p) => ({ ...p, axle: e.target.value }))}>
                                <MenuItem value=""><em>—</em></MenuItem>
                                {axles.map((n) => <MenuItem key={n} value={n}>{i18n.t('vehicleDetail.alarms.axle')}{n}</MenuItem>)}
                            </Select>
                        </FormControl>
                    </Grid>

                    {/* Asignar a — Autocomplete de usuarios */}
                    <Grid item xs={12}>
                        <Autocomplete
                            options={users}
                            getOptionLabel={userLabel}
                            value={users.find((u) => u.id === form.assigned_to) || null}
                            onChange={(_, val) => setForm((p) => ({ ...p, assigned_to: val?.id || '' }))}
                            renderInput={(params) => <TextField {...params} label={t('hardcoded.pages_taskrulespage.asignar_a_opcional')} placeholder={t('hardcoded.pages_chatpage.buscar_usuario_por_nombre_o')} />}
                            isOptionEqualToValue={(o, v) => o.id === v.id}
                        />
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>{t('common.cancel')}</Button>
                <Button variant="contained" onClick={save} disabled={saving} startIcon={saving ? <CircularProgress size={14} /> : <AddIcon />}>
                    {t('hardcoded.components_tickets_ticketactioncenter.crear_tarea')}</Button>
            </DialogActions>
        </Dialog>
    );
};

const TasksPage = () => {
  const { t } = useTranslation();
    const navigate = useNavigate();
    const { showError, showSuccess } = useNotification();
    const [loading, setLoading] = useState(false);
    const [dashboard, setDashboard] = useState(null);
    const [tasks, setTasks] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(25);
    const [filters, setFilters] = useState({
        status: '', priority: '', origin: '', overdue_only: false, search: '',
    });
    const [createOpen, setCreateOpen] = useState(false);

    const loadDashboard = async () => {
        try {
            const r = await api.tasks.dashboard();
            setDashboard(r.data);
        } catch (e) { /* silent */ }
    };

    const loadTasks = async () => {
        setLoading(true);
        try {
            const params = {
                ...filters,
                limit: pageSize,
                offset: page * pageSize,
            };
            Object.keys(params).forEach((k) => (params[k] === '' || params[k] === false) && delete params[k]);
            const r = await api.tasks.list(params);
            setTasks(r.data?.tasks || []);
            setTotal(r.data?.total || 0);
        } catch (e) {
            showError(e?.response?.data?.detail || t('hardcoded.pages_taskspage.error_cargando_tareas'));
        } finally { setLoading(false); }
    };

    useEffect(() => { loadDashboard(); }, []);
    useEffect(() => { loadTasks(); }, [page, pageSize, filters]); // eslint-disable-line

    const handleTransition = async (task, newStatus) => {
        try {
            await api.tasks.transition(task.id, newStatus);
            showSuccess(t('hardcoded.pages_taskspage.tarea_status_labelsne', { STATUS_LABELSne: statusLabel(newStatus) }));
            loadTasks();
            loadDashboard();
        } catch (e) {
            showError(e?.response?.data?.detail || t('hardcoded.pages_taskspage.no_se_pudo_cambiar_el'));
        }
    };

    const kpis = useMemo(() => {
        const d = dashboard || {};
        return [
            { label: t('hardcoded.pages_taskspage.abiertas'), value: d.total_open ?? 0, color: 'primary.main', icon: <ArrowIcon /> },
            { label: t('hardcoded.pages_billinginvoicespage.vencidas'), value: d.overdue ?? 0, color: 'error.main', icon: <ClockIcon /> },
            { label: t('navbar.critical'), value: d.critical ?? 0, color: 'warning.main', icon: <CriticalIcon /> },
            { label: t('hardcoded.pages_taskspage.automaticas'), value: d.by_origin?.automatic ?? 0, color: 'info.main', icon: <AutoIcon /> },
            { label: t('hardcoded.pages_taskspage.manuales'), value: d.by_origin?.manual ?? 0, color: 'success.main', icon: <PersonIcon /> },
        ];
    }, [dashboard]);

    return (
        <Box sx={{ pb: 4 }}>
            <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'center' }} spacing={2} sx={{ mb: 2 }}>
                <Box>
                    <Typography variant="h4" fontWeight={700} color="primary.main">{i18n.t('hardcoded.components_common_sidebar.tareas_aps')}</Typography>
                    <Typography variant="body2" color="text.secondary">
                        {t('hardcoded.pages_taskspage.gestion_seguimiento_y_automatizacion_de')}</Typography>
                </Box>
                <Stack direction="row" spacing={1}>
                    <AskAIButton prompt={t('hardcoded.pages_taskspage.resume_mis_tareas_pendientes_y')} label={t('hardcoded.components_ai_airecommendationspanel.analizar_con_ia')} />
                    <Button variant="outlined" startIcon={<RefreshIcon />} onClick={() => { loadTasks(); loadDashboard(); }}>{i18n.t('audit.actions.UPDATE')}</Button>
                    <Button variant="outlined" onClick={() => navigate('/tasks/rules')}>{t('hardcoded.pages_taskspage.reglas_automaticas')}</Button>
                    <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>{i18n.t('hardcoded.pages_taskspage.nueva_tarea')}</Button>
                </Stack>
            </Stack>

            <AIRecommendationsPanel module="tasks" />

            {/* KPIs */}
            <Grid container spacing={2} sx={{ mb: 2 }}>
                {kpis.map((k) => (
                    <Grid item xs={6} md={2.4} key={k.label}>
                        <Card sx={{ height: '100%', borderRadius: 2 }}>
                            <CardContent>
                                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                                    {React.cloneElement(k.icon, { sx: { color: k.color, fontSize: 22 } })}
                                    <Typography variant="body2" color="text.secondary">{k.label}</Typography>
                                </Stack>
                                <Typography variant="h4" fontWeight={700} color={k.color}>{k.value}</Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
            </Grid>

            {/* Filtros */}
            <Paper sx={{ p: 2, mb: 2 }}>
                <Grid container spacing={2}>
                    <Grid item xs={12} md={3}>
                        <TextField fullWidth size="small" placeholder={t('hardcoded.pages_taskspage.buscar_titulodescripcion')}
                            value={filters.search}
                            onChange={(e) => { setPage(0); setFilters((p) => ({ ...p, search: e.target.value })); }}
                            InputProps={{ startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} /> }} />
                    </Grid>
                    <Grid item xs={6} md={2}>
                        <FormControl fullWidth size="small">
                            <InputLabel shrink>{i18n.t('gps.table.status')}</InputLabel>
                            <Select value={filters.status} displayEmpty notched label={t('gps.table.status')}
                                onChange={(e) => { setPage(0); setFilters((p) => ({ ...p, status: e.target.value })); }}
                                renderValue={(v) => v ? statusLabel(v) : t('hardcoded.pages_taskspage.todos_status')}>
                                <MenuItem value="">{i18n.t('hardcoded.components_alarmprofiles_assignmentstable.todos')}</MenuItem>
                                {statusEntries().map(([v, l]) => <MenuItem key={v} value={v}>{l}</MenuItem>)}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={6} md={2}>
                        <FormControl fullWidth size="small">
                            <InputLabel shrink>{t('hardcoded.components_tickets_ticketactioncenter.prioridad')}</InputLabel>
                            <Select value={filters.priority} displayEmpty notched label={t('hardcoded.components_tickets_ticketactioncenter.prioridad')}
                                onChange={(e) => { setPage(0); setFilters((p) => ({ ...p, priority: e.target.value })); }}
                                renderValue={(v) => v ? priorityLabel(v) : t('hardcoded.pages_taskspage.todas_prioridades')}>
                                <MenuItem value="">{i18n.t('hardcoded.components_settings_notificationpreferenceseditor.todas')}</MenuItem>
                                {priorityEntries().map(([v, l]) => <MenuItem key={v} value={v}>{l}</MenuItem>)}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={6} md={2}>
                        <FormControl fullWidth size="small">
                            <InputLabel shrink>{i18n.t('hardcoded.pages_taskspage.origen')}</InputLabel>
                            <Select value={filters.origin} displayEmpty notched label={t('hardcoded.pages_taskspage.origen')}
                                onChange={(e) => { setPage(0); setFilters((p) => ({ ...p, origin: e.target.value })); }}
                                renderValue={(v) => v === 'manual' ? t('hardcoded.pages_taskspage.manual') : v === 'automatic' ? t('hardcoded.components_settings_adoptionnotificationpreferences.automatica') : 'Todos'}>
                                <MenuItem value="">{i18n.t('hardcoded.components_alarmprofiles_assignmentstable.todos')}</MenuItem>
                                <MenuItem value="manual">{i18n.t('hardcoded.pages_taskspage.manual')}</MenuItem>
                                <MenuItem value="automatic">{t('hardcoded.components_settings_adoptionnotificationpreferences.automatica')}</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={6} md={3}>
                        <Stack direction="row" spacing={1}>
                            <Button variant={filters.overdue_only ? 'contained' : 'outlined'} color="warning"
                                startIcon={<ClockIcon />}
                                onClick={() => { setPage(0); setFilters((p) => ({ ...p, overdue_only: !p.overdue_only })); }}>
                                {t('hardcoded.pages_taskspage.solo_vencidas')}</Button>
                        </Stack>
                    </Grid>
                </Grid>
            </Paper>

            {loading && <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}><CircularProgress size={28} /></Box>}

            {/* Tabla */}
            <Paper>
                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow sx={{ backgroundColor: 'error.main' }}>
                                {[t('hardcoded.components_tickets_ticketactioncenter.titulo'), t('gps.table.status'), t('hardcoded.components_tickets_ticketactioncenter.prioridad'), t('hardcoded.pages_taskspage.origen'), t('vehicles.table.vehicle'), t('gps.card.assigned'), t('hardcoded.pages_billinginvoicespage.vencimiento'), t('hardcoded.pages_taskdetailpage.creada'), t('common.actions')].map((h) => (
                                    <TableCell key={h} sx={{ color: 'common.white', fontWeight: 700, whiteSpace: 'nowrap' }}>{h}</TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {tasks.map((task) => (
                                <TableRow key={task.id} hover sx={{ cursor: 'pointer' }} onClick={() => navigate(`/tasks/${task.id}`)}>
                                    <TableCell>
                                        <Stack spacing={0.3}>
                                            <Typography variant="body2" fontWeight={600}>{task.title}</Typography>
                                            {task.rule_name && <Typography variant="caption" color="info.main">{i18n.t('hardcoded.pages_taskspage.regla')}{task.rule_name}</Typography>}
                                        </Stack>
                                    </TableCell>
                                    <TableCell><Chip size="small" color={STATUS_COLORS[task.status] || 'default'} label={statusLabel(task.status)} /></TableCell>
                                    <TableCell><Chip size="small" color={PRIORITY_COLORS[task.priority] || 'default'} label={priorityLabel(task.priority)} /></TableCell>
                                    <TableCell>
                                        {task.origin === 'automatic'
                                            ? <Chip size="small" icon={<AutoIcon />} label={t("hardcoded.pages_taskspage.auto_label")} color="info" variant="outlined" />
                                            : <Chip size="small" icon={<PersonIcon />} label={t('hardcoded.pages_taskspage.manual')} variant="outlined" />}
                                    </TableCell>
                                    <TableCell>{task.vehicle_name || '—'}{task.position ? t('hardcoded.pages_taskspage.pos_position', { position: task.position }) : ''}</TableCell>
                                    <TableCell>{task.assignee_name || '—'}</TableCell>
                                    <TableCell>
                                        {task.due_at ? (
                                            <Stack direction="row" spacing={0.5} alignItems="center">
                                                <Typography variant="caption" color={task.is_overdue ? 'error.main' : 'text.primary'}>{formatDate(task.due_at)}</Typography>
                                                {task.is_overdue && <WarningIcon sx={{ fontSize: 14, color: 'error.main' }} />}
                                            </Stack>
                                        ) : '—'}
                                    </TableCell>
                                    <TableCell><Typography variant="caption" color="text.secondary">{formatDate(task.created_at)}</Typography></TableCell>
                                    <TableCell onClick={(e) => e.stopPropagation()}>
                                        <Stack direction="row" spacing={0.5}>
                                            {task.status !== 'in_progress' && task.status !== 'completed' && task.status !== 'cancelled' && (
                                                <Tooltip title={t('hardcoded.pages_taskdetailpage.iniciar')}><IconButton size="small" onClick={() => handleTransition(task, 'in_progress')}><StartIcon fontSize="small" /></IconButton></Tooltip>
                                            )}
                                            {task.status !== 'completed' && task.status !== 'cancelled' && (
                                                <Tooltip title={t('hardcoded.pages_taskdetailpage.completar')}><IconButton size="small" color="success" onClick={() => handleTransition(task, 'completed')}><DoneIcon fontSize="small" /></IconButton></Tooltip>
                                            )}
                                            {task.status !== 'cancelled' && task.status !== 'completed' && (
                                                <Tooltip title={t('common.cancel')}><IconButton size="small" color="error" onClick={() => handleTransition(task, 'cancelled')}><CancelIcon fontSize="small" /></IconButton></Tooltip>
                                            )}
                                        </Stack>
                                    </TableCell>
                                </TableRow>
                            ))}
                            {!tasks.length && !loading && (
                                <TableRow><TableCell colSpan={9}><Alert severity="info">{t('hardcoded.pages_taskspage.no_hay_tareas_con_los')}</Alert></TableCell></TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    component="div"
                    count={total}
                    page={page}
                    onPageChange={(_, v) => setPage(v)}
                    rowsPerPage={pageSize}
                    rowsPerPageOptions={[10, 25, 50, 100]}
                    onRowsPerPageChange={(e) => { setPageSize(parseInt(e.target.value, 10)); setPage(0); }}
                />
            </Paper>

            <TaskCreateDialog open={createOpen} onClose={() => setCreateOpen(false)} onCreated={() => { loadTasks(); loadDashboard(); }} />
        </Box>
    );
};

export default TasksPage;
